import { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';

type UseCustomZoomProps = {
  instance: WebViewerInstance | null;
  isLoaded: boolean;
};

/**
 * Custom zoom behavior that leverages PDFTron's built-in zoomToMouse functionality.
 * We need this because PDFTron's built-in zoom system struggles when there is surrounding
 * content on the web page. Extra content on the left or top of the core viewing canvas causes
 * the built-in zoom to mouse functionality to have odd behavior.
 * This implementation uses PDFTron's native zoom system instead of manually calculating
 * scroll positions
 */
export const useCustomZoom = ({ instance, isLoaded }: UseCustomZoomProps) => {
  useEffect(() => {
    if (!instance || !isLoaded) return;

    const { documentViewer } = instance.Core;
    const scrollView = documentViewer.getScrollViewElement();

    if (!scrollView) {
      console.warn('[useCustomZoom] ScrollView element not found');
      return;
    }

    // Track if a zoom operation is in progress to prevent overlapping zooms
    let isZooming = false;

    // Handler to override Command/Ctrl + scroll zoom
    const handleWheel = (event: Event) => {
      const e = event as WheelEvent;

      // Only intercept zoom gestures (Command/Ctrl + scroll)
      const isZoomGesture = e.metaKey || e.ctrlKey;
      if (!isZoomGesture) return;

      // Skip if zoom already in progress to prevent jarring jumps
      if (isZooming) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      isZooming = true;

      const { deltaY } = e;

      // Detect if this is a trackpad or mouse wheel
      // Trackpads typically produce smaller, more frequent deltaY values
      const isTrackpad = Math.abs(deltaY) < 50;

      // Get current zoom level
      const currentZoom = documentViewer.getZoomLevel();

      // Calculate zoom factor with graduated sensitivity
      let zoomFactor: number;

      if (isTrackpad) {
        // Trackpad: more sensitive, smoother increments
        zoomFactor = 1 + -deltaY * 0.005;
      } else {
        // Mouse wheel: less sensitive, graduated based on current zoom
        const baseMultiplier = -deltaY * 0.0008;
        const zoomScale = Math.max(0.5, Math.min(2, currentZoom / 2));
        zoomFactor = 1 + baseMultiplier * zoomScale;
      }

      // Calculate new zoom level with bounds
      const newZoom = Math.max(0.1, Math.min(50, currentZoom * zoomFactor));

      // Calculate offsets to tell PDFTron where UI elements are positioned
      // These offsets are relative to the viewport and help PDFTron's zoomToMouse
      // properly interpret the mouse event coordinates

      const scrollViewRect = scrollView.getBoundingClientRect();
      const webviewerElement = document.querySelector('apryse-webviewer');

      // Calculate horizontal offset (offsetX):
      // Distance from viewport left to where the scroll view starts
      // This accounts for the application's left panels
      // Note: We do NOT add PDFTron's internal left panel width here because
      // PDFTron's coordinate system already accounts for it internally.
      // Adding it would cause zoom to veer toward the panel (same issue as in manual implementation).
      const offsetX = scrollViewRect.left;

      // Calculate vertical offset (offsetY):
      // Distance from viewport top to where document content area starts
      // This includes: application's top elements + PDFTron's toolbar
      let offsetY = scrollViewRect.top;

      // Add PDFTron's toolbar height
      if (webviewerElement) {
        const webviewerRect = webviewerElement.getBoundingClientRect();
        const toolbarHeight = scrollViewRect.top - webviewerRect.top;
        offsetY += toolbarHeight;
      }

      // Use PDFTron's built-in zoomToMouse which handles zoom-to-cursor behavior
      // with proper coordinate transformations, accounting for all UI elements
      documentViewer.zoomToMouse(newZoom, offsetX, offsetY, e);

      // Use requestAnimationFrame to allow PDFTron to complete its zoom animation
      requestAnimationFrame(() => {
        isZooming = false;
      });
    };

    // Add event listener with capture phase to intercept before PDFTron
    scrollView.addEventListener('wheel', handleWheel, {
      passive: false, // Required to call preventDefault
      capture: true, // Capture phase to intercept before PDFTron
    });

    return () => {
      scrollView.removeEventListener('wheel', handleWheel, {
        capture: true,
      } as EventListenerOptions);
    };
  }, [instance, isLoaded]);
};
